/**
 * App Analytics - In-App User Activity Tracking
 * 
 * Tracks logged-in user activity within the application.
 * Includes user_id and organization_id for segmentation.
 * 
 * Usage:
 *   import { initAppAnalytics, trackAppEvent } from './appAnalytics';
 *   
 *   // Initialize after user logs in
 *   useEffect(() => {
 *     if (user) {
 *       initAppAnalytics(user.user_id, user.organization_id);
 *     }
 *   }, [user]);
 */

const ANALYTICS_ENDPOINT = process.env.REACT_APP_BACKEND_URL + '/api/analytics/app/track';

// ==================== SESSION MANAGEMENT ====================

let currentUserId = null;
let currentOrgId = null;

/**
 * Get or create session ID for app usage
 */
const getSessionId = () => {
  if (typeof window === 'undefined') return null;
  
  let sessionId = sessionStorage.getItem('sr_app_session');
  if (!sessionId) {
    sessionId = 'app_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('sr_app_session', sessionId);
  }
  return sessionId;
};

// ==================== TRACKING STATE ====================

let maxScrollDepth = 0;
let pageLoadTime = Date.now();
let lastPagePath = null;
let hasTrackedExit = false;

/**
 * Update max scroll depth
 */
const updateScrollDepth = () => {
  if (typeof window === 'undefined') return;
  
  const scrollTop = window.scrollY || window.pageYOffset;
  const docHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body.scrollHeight
  ) - window.innerHeight;
  
  if (docHeight > 0) {
    const depth = Math.round((scrollTop / docHeight) * 100);
    maxScrollDepth = Math.max(maxScrollDepth, Math.min(depth, 100));
  }
};

// ==================== EVENT SENDING ====================

/**
 * Send analytics event to backend
 */
const sendEvent = async (eventData) => {
  if (typeof window === 'undefined') return;
  
  try {
    const payload = {
      ...eventData,
      session_id: getSessionId(),
      user_id: currentUserId,
      organization_id: currentOrgId,
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || '',
      screen_width: window.screen?.width,
      screen_height: window.screen?.height,
    };
    
    await fetch(ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (e) {
    console.debug('[AppAnalytics] Event send failed:', e);
  }
};

/**
 * Track page exit
 */
const trackExit = () => {
  if (hasTrackedExit) return;
  hasTrackedExit = true;
  
  const timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000);
  
  sendEvent({
    type: 'exit',
    time_on_page: timeOnPage,
    scroll_depth: maxScrollDepth,
    is_exit: true,
  });
};

// ==================== PUBLIC API ====================

/**
 * Initialize app analytics with user context
 */
export const initAppAnalytics = (userId, organizationId) => {
  if (typeof window === 'undefined') return;
  
  currentUserId = userId;
  currentOrgId = organizationId;
  
  // Reset state
  maxScrollDepth = 0;
  pageLoadTime = Date.now();
  hasTrackedExit = false;
  lastPagePath = window.location.pathname;
  
  // Track initial pageview
  sendEvent({ type: 'pageview' });
  
  // Setup scroll tracking
  window.addEventListener('scroll', updateScrollDepth, { passive: true });
  
  // Setup exit tracking
  window.addEventListener('beforeunload', trackExit);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackExit();
    }
  });
  
  return () => {
    window.removeEventListener('scroll', updateScrollDepth);
    window.removeEventListener('beforeunload', trackExit);
  };
};

/**
 * Track page navigation within the app (for SPAs)
 */
export const trackPageView = (pagePath) => {
  if (pagePath === lastPagePath) return;
  
  // Track exit from previous page
  if (lastPagePath) {
    const timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000);
    sendEvent({
      type: 'exit',
      page_path: lastPagePath,
      time_on_page: timeOnPage,
      scroll_depth: maxScrollDepth,
      is_exit: false, // Not a true exit, just navigation
    });
  }
  
  // Reset for new page
  maxScrollDepth = 0;
  pageLoadTime = Date.now();
  lastPagePath = pagePath;
  
  // Track pageview
  sendEvent({ type: 'pageview', page_path: pagePath });
};

/**
 * Track a custom app event
 */
export const trackAppEvent = (eventName, eventData = {}) => {
  sendEvent({
    type: 'event',
    event_name: eventName,
    event_data: eventData,
  });
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = (featureName, action, metadata = {}) => {
  sendEvent({
    type: 'feature',
    event_name: `feature_${featureName}_${action}`,
    event_data: {
      feature: featureName,
      action: action,
      ...metadata,
    },
  });
};

/**
 * Update user context (e.g., after org switch)
 */
export const updateUserContext = (userId, organizationId) => {
  currentUserId = userId;
  currentOrgId = organizationId;
};

export default {
  initAppAnalytics,
  trackPageView,
  trackAppEvent,
  trackFeatureUsage,
  updateUserContext,
};
